/**
 * Author: Romain Hapi Sr.
 * Version: 2.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
/**
 * Liste des services du site Tchad.
 *
 * Le catalogue vient de `serviceCatalog.js`, qui porte pour chaque service son
 * slug, son icone et sa teinte ; les libelles, eux, viennent des traductions.
 * Separer les deux evite de melanger presentation et contenu traduisible.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { PageHero } from '../components/Section';
import { useReveal } from '../hooks/useReveal';
import SEO from '../../components/SEO';
import { SERVICES, LIFT } from '../serviceCatalog';
import { SHELL, SECTION, H2, LEAD, BTN_PRIMARY, BTN_ON_LIGHT, RULE } from '../theme';

const META = {
  fr: {
    title: 'Services — Prenim Tchad',
    description:
      "Transformation numerique, conseil, integration, developpement, DevOps et infrastructure reseau a N'Djamena et en zone CEMAC.",
  },
  en: {
    title: 'Services — Prenim Tchad',
    description:
      "Digital transformation, consulting, integration, development, DevOps and network infrastructure in N'Djamena and the CEMAC area.",
  },
  ar: {
    title: 'الخدمات — Prenim Tchad',
    description: 'التحول الرقمي والاستشارات والتكامل والتطوير و DevOps والبنية التحتية للشبكات.',
  },
};

const EYEBROW = { fr: 'Nos expertises', en: 'Our expertise', ar: 'خبراتنا' };
const CTA_LEAD = {
  fr: 'Parlons de votre contexte avant de parler de technologie.',
  en: 'Let us talk about your context before we talk about technology.',
  ar: 'لنتحدث عن سياقكم قبل الحديث عن التقنية.',
};

export const Services = () => {
  const { t, language } = useLanguage();
  const meta = META[language] || META.fr;
  const grid = useReveal();
  const cta = useReveal();

  return (
    <main data-testid="tchad-services-page">
      <SEO title={meta.title} description={meta.description} lang={language} />

      <PageHero
        eyebrow={EYEBROW[language] || EYEBROW.fr}
        title={t.services.title}
        lead={t.services.subtitle}
      />

      <section className={`${SECTION} bg-white`}>
        <div className={SHELL}>
          <div ref={grid.ref} className={`${grid.className} grid gap-6 md:grid-cols-2`}>
            {SERVICES.map((service) => {
              const data = t.services[service.key];
              const { Icon } = service;
              if (!data) return null;
              return (
                <Link
                  key={service.key}
                  to={`/tchad/services/${service.slug}`}
                  data-testid={`tchad-service-card-${service.key}`}
                  className={`group relative z-0 flex flex-col overflow-hidden rounded-lg border border-slate-200/80 p-8 lg:p-10 ${LIFT} ${service.bg} ${service.hover}`}
                >
                  <span
                    aria-hidden="true"
                    className={`absolute inset-x-0 top-0 h-1 ${service.rule}`}
                  />
                  <Icon className={`h-7 w-7 ${service.icon}`} strokeWidth={1.5} />
                  <h2 className="mt-6 text-2xl font-bold tracking-tight text-slate-950 lg:text-3xl">
                    {data.name}
                  </h2>
                  <p className="mt-4 leading-relaxed text-slate-700">{data.description}</p>

                  <ul className="mt-6 space-y-3">
                    {service.features.map((feature) => (
                      <li
                        key={feature.fr}
                        className="flex items-start gap-3 text-sm text-slate-700"
                      >
                        <span
                          aria-hidden="true"
                          className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${service.dot}`}
                        />
                        <span>{feature[language] || feature.fr}</span>
                      </li>
                    ))}
                  </ul>

                  <span className="mt-8 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-950">
                    {t.projects.viewDetails}
                    <ArrowRight className="h-3.5 w-3.5 rtl-flip transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className={`${SECTION} bg-slate-50`}>
        <div className={SHELL}>
          <div
            ref={cta.ref}
            className={`${cta.className} grid items-center gap-10 rounded-lg border border-slate-200 bg-white p-10 lg:grid-cols-12 lg:p-16`}
          >
            <div className="lg:col-span-7">
              <div className={RULE} />
              <h2 className={`${H2} mt-5 text-slate-950`}>{t.contact.subtitle}</h2>
              <p className={`mt-5 max-w-xl ${LEAD}`}>{CTA_LEAD[language] || CTA_LEAD.fr}</p>
            </div>
            <div className="flex flex-wrap gap-3 lg:col-span-5 lg:justify-end">
              <Link to="/tchad/contact" data-testid="tchad-services-cta" className={BTN_PRIMARY}>
                {t.hero.ctaSecondary}
                <ArrowRight className="h-4 w-4 rtl-flip" />
              </Link>
              <Link to="/tchad/projects" className={BTN_ON_LIGHT}>
                {t.nav.projects}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
